<template>
    <Fieldset style="position:relative">
        <template #legend>
            <div class="flex items-center text-primary">
                <span class="font-bold text-lg">{{ title }}</span>
                <LoadingState v-if="saving" small></LoadingState>
            </div>
        </template>
        <div v-if="leadingObjectTable">
            <Message v-if="showWorkspaceLevelFiltersMessage" severity="info" class="mb-3">
                Filters have been set at workspace level.
            </Message>
            <div class="flex items-start justify-between gap-2">
                <div class="filtersSummary">{{ filtersSummary }}</div>
                <Button rounded label="Edit Filters" icon="pi pi-pencil" @click="openExpressionManager"/>
            </div>
            <div class="mt-4">
                <Message v-if="errorMessage" severity="error">{{ errorMessage }}</Message>
            </div>
        </div>
        <div v-else>
            <Message severity="warn">{{ noLeadingObjectMessage }}</Message>
        </div>
    </Fieldset>

    <ExpressionManager
        v-if="expressionEditorVisible"
        :visible="expressionEditorVisible"
        :modelValue="expressionDraft"
        elementType="filter"
        @update:modelValue="(value) => expressionDraft = value"
        @save="saveExpressionFilters"
        @update:visible="(value) => expressionEditorVisible = value"
        @close="onExpressionManagerClose"
        :defaultTable="leadingObjectTable"
    />
</template>

<script setup>
import {computed, ref, watch} from 'vue'
import _ from 'lodash-es'
import {expressionToString} from '@/lib/util/ModelUtil'
import {collectExpressions, toFilterList} from '@/src/utils/caseTableFilters'
import ExpressionManager from '@/src/views/graph/elements/ExpressionManager.vue'

const props = defineProps({
    modelValue: {
        type: [Array, Object],
        default: () => []
    },
    leadingObjectTable: {
        type: String,
        default: ''
    },
    saving: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: 'Custom Case Table Filters'
    },
    noLeadingObjectMessage: {
        type: String,
        default: 'No leading object table defined.'
    },
    errorMessage: {
        type: String,
        default: ''
    },
    showWorkspaceLevelFiltersMessage: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:modelValue', 'save'])

const localFilters = ref([])
const expressionEditorVisible = ref(false)
const expressionDraft = ref(null)

const createExpressionId = (index) => `case-filter-${Date.now()}-${index}`

const cloneFilters = (filters = []) => _.cloneDeep(filters)

const normalizeFilterState = (filter) => {
    const valid = Object.prototype.hasOwnProperty.call(filter, 'valid') ? Boolean(filter.valid) : Boolean(filter.enabled)
    return {
        ...filter,
        valid,
        enabled: valid,
        'event_attribute.table': filter['event_attribute.table'] || props.leadingObjectTable
    }
}

const createExpressionDraft = (filters = []) => ({
    type: 'group',
    condition: 'AND',
    expressions: cloneFilters(filters).map((filter, index) => {
        const id = filter.id || createExpressionId(index)
        return {
            id,
            type: 'simple',
            expression: {
                ...filter,
                id
            }
        }
    })
})

watch(
    () => props.modelValue,
    (value) => {
        localFilters.value = cloneFilters(toFilterList(value)).map(filter => ({
            ...filter,
            valid: Object.prototype.hasOwnProperty.call(filter, 'valid') ? Boolean(filter.valid) : Boolean(filter.enabled)
        }))
    },
    {immediate: true, deep: true}
)

const filtersSummary = computed(() => {
    if (!localFilters.value.length) {
        return 'No custom case table filters configured.'
    }
    return localFilters.value.filter(filter => filter.valid).map(filter => expressionToString(filter)).join(' • ')
})

const emitUpdate = (payload) => {
    emit('update:modelValue', payload)
    emit('save', payload)
}

const openExpressionManager = () => {
    expressionDraft.value = createExpressionDraft(localFilters.value)
    expressionEditorVisible.value = true
}

const onExpressionManagerClose = () => {
    expressionEditorVisible.value = false
}

const saveExpressionFilters = (expressionValue) => {
    const filtersFromExpression = collectExpressions(expressionValue || expressionDraft.value)
        .map(filter => _.omit(filter, ['id']))
        .map(normalizeFilterState)
    localFilters.value = filtersFromExpression
    emitUpdate(localFilters.value)
    expressionEditorVisible.value = false
}
</script>

<style scoped>
.filtersSummary {
    flex: 1;
    overflow-wrap: anywhere;
}
</style>
